import type { CSSProperties } from 'react';

import type { CompanyDocument, FeedPost } from '~/api/documents';
import type { Tenant } from '~/api/tenant';
import { OfficeLogo } from '~/components/OfficeLogo';
import { Icon } from '~/components/ui/Icon';
import { formatDateTime, formatRelativeTime } from '~/lib/format';
import { GuideStatusTag, guideSummary } from '~/pages/impostos/GuideCard';

const OFFICE_LOGO_SIZE = 32;
const HAIRLINE = '0.5px solid var(--rx-border)';

const HEADLINE_STYLE: CSSProperties = {
  margin: '12px 0 0',
  fontSize: 15,
  fontWeight: 500,
  color: 'var(--rx-text)',
  letterSpacing: '-0.2px',
};

const DOCUMENT_LIST_STYLE: CSSProperties = { listStyle: 'none', margin: '8px 0 0', padding: 0 };

const DOCUMENT_ROW_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  padding: '10px 0',
  background: 'transparent',
  border: 'none',
  color: 'inherit',
  fontFamily: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',
};

const DOCUMENT_TEXT_STYLE: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

const DOCUMENT_TITLE_STYLE: CSSProperties = {
  display: 'block',
  fontSize: 13,
  color: 'var(--rx-text)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const GUIDE_SUMMARY_STYLE: CSSProperties = {
  display: 'block',
  fontSize: 12,
  color: 'var(--rx-text-2)',
};

/** O nome com que o escritório assina os posts: o curto, quando ele tem um. */
export function officeShortName(tenant: Tenant): string {
  return tenant.nome_curto || tenant.nome;
}

function headlineIdOf(post: FeedPost): string {
  return `post-${post.id}-manchete`;
}

interface PostCardProps {
  post: FeedPost;
  tenant: Tenant;
  /** Publicado depois da última visita ao feed desta empresa. */
  isNew: boolean;
  /** O documento aberto no visualizador, que a linha marca como o atual. */
  openedDocumentId: string | null;
  onOpenDocument(companyDocument: CompanyDocument): void;
}

/**
 * Um post do feed: o escritório que publicou (logo e nome curto), quando, a manchete e uma linha
 * por documento, com "vencida" na certidão que passou da validade e "substituído" no documento
 * que outro mais novo trocou. A linha abre o documento no visualizador.
 */
export function PostCard({
  post,
  tenant,
  isNew,
  openedDocumentId,
  onOpenDocument,
}: PostCardProps): JSX.Element {
  const headlineId = headlineIdOf(post);

  return (
    <article aria-labelledby={headlineId} className="rx-card">
      <header className="flex items-center gap-2.5">
        <span aria-hidden="true" className="shrink-0">
          <OfficeLogo tenant={tenant} size={OFFICE_LOGO_SIZE} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-medium text-ink">{officeShortName(tenant)}</div>
          <time
            dateTime={post.created_at}
            title={formatDateTime(post.created_at)}
            className="text-[11px] text-ink-subtle"
          >
            {formatRelativeTime(post.created_at)}
          </time>
        </div>
        {isNew && <span className="rx-tag rx-tag-brand">Novo</span>}
      </header>

      <h2 id={headlineId} style={HEADLINE_STYLE}>
        {post.headline}
      </h2>

      <ul style={DOCUMENT_LIST_STYLE}>
        {post.documents.map((companyDocument, index) => (
          <li key={companyDocument.id} style={{ borderTop: index === 0 ? 'none' : HAIRLINE }}>
            <DocumentRow
              companyDocument={companyDocument}
              opened={companyDocument.id === openedDocumentId}
              onOpen={onOpenDocument}
            />
          </li>
        ))}
      </ul>
    </article>
  );
}

interface DocumentRowProps {
  companyDocument: CompanyDocument;
  opened: boolean;
  onOpen(companyDocument: CompanyDocument): void;
}

/**
 * Uma linha de documento, no post e na pasta: o título, "vencida" e "substituído". Na guia (E16),
 * abaixo do título, o valor e o vencimento, e o selo da situação; o "Paguei" fica na tela
 * "Impostos" e no visualizador. Tocar na linha abre o documento no visualizador.
 */
export function DocumentRow({ companyDocument, opened, onOpen }: DocumentRowProps): JSX.Element {
  const { guide } = companyDocument;

  return (
    <button
      type="button"
      onClick={() => onOpen(companyDocument)}
      aria-current={opened ? 'true' : undefined}
      style={DOCUMENT_ROW_STYLE}
    >
      <span aria-hidden="true" className="text-brand">
        <Icon name="doc" size={16} />
      </span>
      <span style={DOCUMENT_TEXT_STYLE}>
        <span style={DOCUMENT_TITLE_STYLE}>{companyDocument.title}</span>
        {guide && <span style={GUIDE_SUMMARY_STYLE}>{guideSummary(guide)}</span>}
      </span>
      {guide && <GuideStatusTag status={guide.status} />}
      {companyDocument.is_expired && <span className="rx-tag rx-tag-neg">vencida</span>}
      {!companyDocument.is_current && <span className="rx-tag">substituído</span>}
      <span aria-hidden="true">
        <Icon name="chevron" size={14} color="var(--rx-text-3)" />
      </span>
    </button>
  );
}
